/**
 * Package-aware feature → tab engine.
 *
 * Shared by /e/:slug (GuestLanding) and /event/:slug/guest (GuestHome)
 * so the two QR entry points behave IDENTICALLY. Do not inline these
 * maps in pages — import from here. The backend's FEATURES_CATALOG +
 * FEATURE_FLAG_MAP (server.py) MUST stay in lockstep with this module.
 *
 * Contract:
 *   - features[]  = ownership ledger from the backend (authoritative)
 *   - tabs        = derived via buildTabs(features)
 *   - Never render a tab/card for a feature NOT in features[]
 *   - No "locked", "upgrade", "coming soon" states anywhere
 */

import { useState, useEffect } from 'react';

export const FEATURE_TAB_MAP = {
  qr_upload:        'upload',
  memory_guestbook: 'guestbook',
  video_guestbook:  'video',
  pocket_booth:     'booth',
  spin_film_360:    'spin360',
  memory_snap:      'memory_snap',
  photo_booth:      'booth',
  highlight_reel:   'highlights',
  full_event_film:  'highlights',
  challenge_mode:   'challenges',
  // booth_os is intentionally NOT in this map. It opens its own
  // fullscreen kiosk surface at /booth-os/:event_id, not a tab
  // inside the guest's event page. The host launches it from the
  // Setup tab's BoothPairingPanel; guests interact at the venue.
};

export const TAB_ORDER = ['upload', 'guestbook', 'video', 'booth', 'spin360', 'memory_snap', 'challenges', 'highlights'];

// Human-readable labels for the tab pill + the "✨ This event includes…" strip.
export const TAB_LABEL = {
  upload:      'Upload',
  guestbook:   'Leave a memory',
  video:       'Video',
  booth:       'Booth',
  spin360:     'Spin Film 360',
  memory_snap: 'Memory Snap',
  challenges:  'Challenges',
  highlights:  'Highlights',
};

export const TAB_VALUE_LABEL = {
  upload:      'Upload',
  guestbook:   'Memory Messages',
  video:       'Messages they\'ll keep forever',
  booth:       'Pocket Booth',
  spin360:     'Spin Film 360',
  memory_snap: 'Memory Snap',
  challenges:  'Challenges',
  highlights:  'Highlights',
};

export function buildTabs(features) {
  const set = new Set(['upload']); // always
  (features || []).forEach((f) => {
    const tab = FEATURE_TAB_MAP[f];
    if (tab) set.add(tab);
  });
  return [...set].sort((a, b) => TAB_ORDER.indexOf(a) - TAB_ORDER.indexOf(b));
}

// Per-feature metadata for the tab panels. Keep copy short — guests
// are already one tap from the real experience.
//
// Card refactor (2026-05-12): added `subtitle` (the one-line emotional
// descriptor used by ProductCard) and `accent` (semantic colour key
// for the per-product glow). Existing `gradient` retained for any
// legacy surface that still renders the stacked-pill launcher — both
// fields can coexist until that path is fully retired.
export const FEATURE_CARDS = {
  memory_snap: {
    title: 'Memory Snap',
    subtitle: 'A polaroid moment from each guest.',
    copy: 'A polaroid moment from each guest.',
    cta: 'Open Memory Snap',
    iconName: 'ImageSquare',
    accent: 'gold',
    gradient: 'linear-gradient(135deg, #D4AF5E, #8C6A2B)',
    route: (slug) => `/event/${slug}/memory-snap`,
    engagementKey: 'memory_snap',
    countNoun: { one: 'memory snap', many: 'memory snaps' },
  },
  memory_wall_card: {
    title: 'Memory Wall',
    subtitle: 'A living wall of every photo and message.',
    copy: 'A living wall of every photo and message.',
    cta: 'Open Memory Wall',
    iconName: 'ImageSquare',
    accent: 'gold',
    gradient: 'linear-gradient(135deg, #D4AF5E, #8C6A2B)',
    route: (slug) => `/event/${slug}/memory-wall`,
  },
  highlight_reel: {
    title: 'Highlight Reel',
    subtitle: 'A short, composed cut of the night.',
    copy: 'A short, composed cut of the night.',
    cta: 'See the highlights',
    iconName: 'Play',
    accent: 'gold',
    gradient: 'linear-gradient(135deg, #D4AF5E, #8C6A2B)',
    // Until the dedicated render surface ships, land on the wall
    // with a quiet "Your highlight reel is being composed ✦" notice
    // so the card never dead-ends.
    route: (slug) => `/event/${slug}/memory-wall?notice=highlight-composing`,
  },
  recap_letters: {
    title: 'Recap Letters',
    subtitle: 'A letter to every guest after the night settles.',
    copy: 'A letter to every guest after the night settles.',
    cta: 'Read the letters',
    iconName: 'Envelope',
    accent: 'gold',
    gradient: 'linear-gradient(135deg, #D4AF5E, #8C6A2B)',
    route: (slug) => `/event/${slug}/memory-recap`,
  },
  archive_shelf: {
    title: 'Archive Shelf',
    subtitle: 'Chapters and letters from the night, on a shelf.',
    copy: 'Chapters and letters from the night, on a shelf.',
    cta: 'Open the archive',
    iconName: 'BookOpen',
    accent: 'gold',
    gradient: 'linear-gradient(135deg, #D4AF5E, #8C6A2B)',
    route: (slug) => `/event/${slug}/archive`,
  },
  qr_upload: {
    title: 'QR Upload',
    subtitle: 'Guests scan and add photos and videos to the wall.',
    copy: 'Guests scan and add photos and videos to the wall.',
    cta: 'Open uploads',
    iconName: 'QrCode',
    accent: 'gold',
    gradient: 'linear-gradient(135deg, #D4AF5E, #8C6A2B)',
    route: (slug) => `/event/${slug}/memory-wall?focus=qr`,
  },
  booth_os: {
    // The Room+ marquee feature — the venue kiosk. A tablet at the
    // room with the four-card launcher (Capture · Tell · Memory
    // Snap · Guestbook). NOT a tab inside the guest page — opens
    // its own fullscreen surface at /booth-os/:event_id. The host
    // pairs it once via the 6-digit code in Setup → On-venue
    // instruments. Guests just step up to the tablet on the night.
    title: 'Booth OS',
    subtitle: 'Venue kiosk experience for the tablet at the room.',
    copy: 'Photos, video messages, Memory Snaps and the Guestbook — all from a real tablet at the venue.',
    cta: 'Open Booth OS',
    iconName: 'AppWindow',
    accent: 'gold',
    gradient: 'linear-gradient(135deg, #D4AF5E, #8C6A2B)',
    // route() opens the kiosk surface itself in a new context.
    // We use event_id rather than slug because BoothOSPage keys
    // every booth read off the event_id (matches the pairing
    // contract — `/booth-os/:event_id`).
    route: (slug, event) => `/booth-os/${event?.event_id || slug}`,
  },
  pocket_booth: {
    title: 'Pocket Booth',
    subtitle: 'Phone-based booth guests open anywhere during the night.',
    copy: 'A phone-based booth every guest can open from their own screen — no kiosk required.',
    cta: 'Open Pocket Booth',
    iconName: 'Camera',
    accent: 'pink',
    gradient: 'linear-gradient(135deg, #FF0099, #B89762)',
    route: (slug) => `/event/${slug}/pocket-booth`,
    engagementKey: 'pocket_booth',
    countNoun: { one: 'strip', many: 'strips' },
  },
  video_guestbook: {
    title: 'Audio / Video Guestbook',
    subtitle: 'Guests record a short message to the night.',
    copy: 'Guests record a short message to the night.',
    cta: 'Record a message',
    iconName: 'Microphone',
    accent: 'teal',
    gradient: 'linear-gradient(135deg, #26A69A, #C29D52)',
    route: (slug) => `/event/${slug}/guestbook`,
    engagementKey: 'video_guestbook',
    countNoun: { one: 'message left', many: 'messages left' },
  },
  challenge_mode: {
    title: 'Mega Missions',
    subtitle: 'Timed quests that stir the room into motion.',
    copy: 'Timed quests that stir the room into motion.',
    cta: 'Take a mission',
    iconName: 'Lightning',
    accent: 'violet',
    gradient: 'linear-gradient(135deg, #B89762, #FF0099)',
    route: (slug) => `/event/${slug}/challenges`,
    engagementKey: 'challenge_mode',
    countNoun: { one: 'mission done', many: 'missions done' },
  },
  invites: {
    title: 'Invites & RSVP',
    subtitle: 'Sent quietly, returned with intent.',
    copy: 'Sent quietly, returned with intent.',
    cta: 'Open invites',
    iconName: 'Envelope',
    accent: 'cyan',
    gradient: 'linear-gradient(135deg, #B89762, #C29D52)',
    route: (slug) => `/event/${slug}/invites`,
  },
  spin_film_360: {
    title: 'Spin Film 360',
    subtitle: 'A slow-motion 360 spin for every guest.',
    copy: 'A slow-motion 360 spin for every guest.',
    cta: 'Open Spin Film 360',
    iconName: 'Camera',
    accent: 'gold',
    gradient: 'linear-gradient(135deg, #FF6BCB, #C29D52)',
    route: () => `/spin`,
    engagementKey: 'spin_film_360',
    countNoun: { one: 'spin', many: 'spins' },
  },
  full_event_film: {
    title: 'Event Film',
    subtitle: 'A composed cinematic record of the night.',
    copy: 'A composed cinematic record of the night.',
    cta: 'Watch the film',
    iconName: 'FilmStrip',
    accent: 'violet',
    gradient: 'linear-gradient(135deg, #A033D8, #B89762)',
    route: (slug) => `/film/${slug}`,
  },
  // Lens Notes ✦ — Archive-exclusive (iter 168). Premium memory architecture:
  // handwriting over video. Positioned HIGH inside Archive (just under
  // Event Film) because once guests see it they immediately understand
  // the tier difference.
  lens_notes: {
    title: 'Lens Notes ✦',
    subtitle: 'Handwritten video memories from the people who lived the night beside you.',
    copy: 'Guests record short videos while writing directly onto the memory itself ✦',
    cta: 'Leave a Lens Note ✦',
    iconName: 'PencilSimple',
    accent: 'gold',
    gradient: 'linear-gradient(135deg, #C19A4B, #6B4A22)',
    route: (slug) => `/lens-note/${slug}`,
    engagementKey: 'lens_notes',
    countNoun: { one: 'lens note', many: 'lens notes' },
  },
  // Drop One Word ✦ — universal across all editions (iter 170). One word,
  // one phrase, one feeling. Forms a living typography wall.
  drop_one_word: {
    title: 'Drop One Word ✦',
    subtitle: 'A living atmosphere built from the feelings shared throughout the night.',
    copy: 'Guests leave one word, one phrase or one feeling ✦',
    cta: 'Leave A Word ✦',
    iconName: 'Quotes',
    accent: 'gold',
    gradient: 'linear-gradient(135deg, #C19A4B, #6B4A22)',
    route: (slug) => `/one-word/${slug}`,
    engagementKey: 'drop_one_word',
    countNoun: { one: 'word', many: 'words' },
  },
  memory_guestbook: {
    title: 'Memory Guestbook',
    subtitle: 'A printed keepsake of the night, mailed afterwards.',
    copy: 'A printed keepsake of the night, mailed afterwards.',
    cta: 'Add to the book',
    iconName: 'BookOpen',
    accent: 'cyan',
    gradient: 'linear-gradient(135deg, #D946EF, #B89762)',
    route: (slug) => `/event/${slug}/memory-book-message`,
    engagementKey: 'memory_guestbook',
    countNoun: { one: 'message', many: 'messages' },
  },
  // Photo Booth — kept for legacy events that bought it; no longer
  // surfaced through the new editions but visible if the event has
  // photo_booth_purchased: true.
  photo_booth: {
    title: 'Photo Booth',
    subtitle: 'Grab the group, take the shot.',
    copy: 'Grab the group, take the shot.',
    cta: 'Open Photo Booth',
    iconName: 'Camera',
    accent: 'cyan',
    gradient: 'linear-gradient(135deg, #B89762, #C29D52)',
    route: (slug) => `/event/${slug}/booth`,
    engagementKey: 'photo_booth',
    countNoun: { one: 'shot', many: 'shots' },
  },
};

// ──────────────────────────────────────────────────────────────────────
// LIVE ENGAGEMENT COUNTERS — social-proof copy under each launcher
// ("🔥 Spin Film 360 — 24 boomerangs already"). Single helper consumed
// by GuestHome, GuestLanding, and GuestDashboard so the formatting +
// threshold rules stay consistent across all three entry points.
// ──────────────────────────────────────────────────────────────────────

// Below this threshold we DON'T show a counter — "1 boomerang already"
// reads as embarrassing rather than social-proof. Once a single guest
// hits 3 we have enough momentum to show the number publicly.
export const ENGAGEMENT_MIN_DISPLAY = 3;

// Returns the rendered counter string (or `null` to render nothing).
// Counts are zero-aware: if backend returned `null`/`undefined` we
// treat as 0 and skip the counter so the button doesn't flash on +
// off when the API races the page render.
export function engagementCounterText(feature, counts) {
  if (!counts) return null;
  const card = FEATURE_CARDS[feature];
  if (!card || !card.engagementKey || !card.countNoun) return null;
  const n = counts[card.engagementKey] || 0;
  if (n < ENGAGEMENT_MIN_DISPLAY) return null;
  const noun = n === 1 ? card.countNoun.one : card.countNoun.many;
  return `🔥 ${n.toLocaleString()} ${noun} already`;
}

// React hook — fetch + auto-refresh engagement counts for an event.
// Refreshes every 25s while the page is open (slightly off-cycle from
// any common 30s polling so we don't all hit at once on a kiosk wall).
// Returns `{counts, loading}`. `counts` is null until the first fetch
// completes — `engagementCounterText` already no-ops on null so no
// flicker on initial render.
export function useEngagement(eventId, axios, apiBase) {
  const [counts, setCounts] = useState(null);
  useEffect(() => {
    if (!eventId || !axios || !apiBase) return undefined;
    let cancelled = false;
    const fetchOnce = async () => {
      try {
        const r = await axios.get(`${apiBase}/events/${eventId}/engagement`);
        if (!cancelled) setCounts(r.data);
      } catch {
        /* fire-and-forget; counter just stays hidden */
      }
    };
    fetchOnce();
    const id = setInterval(fetchOnce, 25000);
    return () => { cancelled = true; clearInterval(id); };
  }, [eventId, axios, apiBase]);
  return { counts };
}

// ──────────────────────────────────────────────────────────────────────
// EVENT LIVE COUNTER PILLS — "🔥 184 moments shared tonight" pills
// rendered under the event title on guest pages. Mirrors the platform-
// wide pills on the homepage hero but pulled from the per-event
// engagement endpoint. Hides any metric below MIN_DISPLAY so a brand-
// new event doesn't read "0 moments" / "0 spins".
// ──────────────────────────────────────────────────────────────────────
const EVENT_PILL_MIN = 3;
export function eventLivePillsFromCounts(counts) {
  if (!counts) return [];
  const all = [
    { key: 'qr_upload', emoji: '📸', label: 'moments shared tonight', value: counts.qr_upload, accent: '#D6B25E' },
    { key: 'spin_film_360', emoji: '🎥', label: 'spins created', value: counts.spin_film_360, accent: '#00D4FF' },
    { key: 'memory_snap', emoji: '🖼️', label: 'memory snaps pinned', value: counts.memory_snap, accent: '#D4AF5E' },
    { key: 'pocket_booth', emoji: '👜', label: 'pocket strips taken', value: counts.pocket_booth, accent: '#FF6BCB' },
    { key: 'memory_guestbook', emoji: '💌', label: 'memories left', value: counts.memory_guestbook, accent: '#D946EF' },
    { key: 'video_guestbook', emoji: '🎙️', label: 'messages recorded', value: counts.video_guestbook, accent: '#B89762' },
    { key: 'challenge_mode', emoji: '🏆', label: 'challenges completed', value: counts.challenge_mode, accent: '#FCD34D' },
  ];
  return all.filter((p) => (p.value || 0) >= EVENT_PILL_MIN);
}

// Client-side dedup-aware activator. Caller owns the Set instance so
// it persists for the page session (dedup) but doesn't leak across
// mounts.
export async function activateFeature(axios, apiBase, eventId, feature, sessionSet) {
  if (!eventId || !feature) return;
  if (sessionSet && sessionSet.has(feature)) return;
  if (sessionSet) sessionSet.add(feature);
  try {
    await axios.post(`${apiBase}/events/${eventId}/activate-feature`, { feature });
  } catch {
    /* fire-and-forget; backend is idempotent */
  }
}

// ──────────────────────────────────────────────────────────────────────
// DISCOVERY + COMPLETION LOOP (Apr 2026)
//
// The shared engagement layer that turns "upload and leave" into "oh
// I've done 2/5 — what's next?". Two complementary tracks:
//
//   * NEW pip — "you haven't opened this tab yet" whisper, scoped to
//     HIGH_VALUE_FEATURES so we never spam. Auto-clears after the
//     guest has explored ≥2 high-value tabs.
//   * Completion — tracks MEANINGFUL interactions (submitted a message,
//     recorded a video, captured a booth strip, uploaded). Drives the
//     progress bar + next-best-action hint + "almost there" nudge.
//
// Both keyed by SLUG only (not anon_id) — different guests on the same
// device share the nudge state, which is fine: the goal is event-level
// engagement, not per-person tracking.
// ──────────────────────────────────────────────────────────────────────

// High-value features that earn a NEW pip. Upload is excluded — the
// guest is already inside the Upload tab by default. Highlights/film
// are passive (guest watches, doesn't participate).
export const HIGH_VALUE_FEATURES = [
  'memory_guestbook',
  'video_guestbook',
  'challenge_mode',
  'pocket_booth',
  'memory_snap',
];

// Launch dates for new features — drives a per-feature 30-day window
// for the subtle gold "NEW" shimmer. Anything not listed here uses
// the standard HIGH_VALUE_FEATURES rules (seen/unseen + cap of 2).
// Once a feature's been live >30d we let the pip retire even for
// guests who never opened it — at that point it's no longer "new".
export const FEATURE_LAUNCH_DATES = {
  memory_snap: '2026-05-12',
};
const NEW_WINDOW_MS = 30 * 24 * 60 * 60 * 1000;

// COMPLETION_FEATURES lists the experiences that count toward the
// "2/5 unlocked" counter. Order matters — the next-best-action hint
// picks the first incomplete+owned entry.
export const COMPLETION_FEATURES = [
  'upload',
  'memory_guestbook',
  'video_guestbook',
  'pocket_booth',
  'memory_snap',
  'challenge_mode',
];

export const COMPLETION_EMOJI = {
  upload:           '📸',
  memory_guestbook: '💌',
  video_guestbook:  '🎥',
  pocket_booth:     '🎞️',
  memory_snap:      '🖼️',
  challenge_mode:   '⚡',
};

const SEEN_KEY = (slug) => `event_seen_features_${slug}`;
const DONE_KEY = (slug) => `event_completion_${slug}`;

function readJSON(key) {
  try { return JSON.parse(localStorage.getItem(key) || '{}') || {}; }
  catch { return {}; }
}
function writeJSON(key, obj) {
  try { localStorage.setItem(key, JSON.stringify(obj)); } catch { /* quota / private mode */ }
}

export function getSeenFeatures(slug) { return readJSON(SEEN_KEY(slug)); }
export function markFeatureSeen(slug, feature) {
  const seen = getSeenFeatures(slug);
  if (seen[feature]) return seen;
  seen[feature] = true;
  writeJSON(SEEN_KEY(slug), seen);
  return seen;
}
export function shouldShowNewPip(slug, feature) {
  if (!HIGH_VALUE_FEATURES.includes(feature)) return false;
  // Per-feature launch-date window — once a feature has been live for
  // more than NEW_WINDOW_MS, retire its pip globally (no more whispers,
  // even for guests who never opened it). Older features without a
  // launch date keep the legacy seen/unseen behaviour below.
  const launch = FEATURE_LAUNCH_DATES[feature];
  if (launch) {
    const launchedAt = Date.parse(launch);
    if (Number.isFinite(launchedAt) && Date.now() - launchedAt > NEW_WINDOW_MS) return false;
  }
  const seen = getSeenFeatures(slug);
  if (seen[feature]) return false;
  // Auto-clear once the guest has explored ≥2 high-value tabs — at
  // that point they "get it", more pips would be noise.
  const seenHighValue = HIGH_VALUE_FEATURES.filter((f) => seen[f]).length;
  if (seenHighValue >= 2) return false;
  return true;
}

// pickNewPipFeature — guest pages cap visible NEW pips to ONE per the
// brand-doc §5.1 rule. When multiple features are eligible, prefer the
// one with an explicit recent launch date (so a freshly-shipped product
// like Memory Snap reliably wins the pip vs. older HIGH_VALUE entries).
// Falls back to first-eligible-by-tab-order for the legacy case.
export function pickNewPipFeature(slug, features) {
  const eligible = (features || []).filter((f) => shouldShowNewPip(slug, f));
  if (!eligible.length) return null;
  // Sort: most-recent launch date first, undated features last.
  eligible.sort((a, b) => {
    const ta = Date.parse(FEATURE_LAUNCH_DATES[a] || '') || 0;
    const tb = Date.parse(FEATURE_LAUNCH_DATES[b] || '') || 0;
    return tb - ta;
  });
  return eligible[0];
}

export function getCompletion(slug) { return readJSON(DONE_KEY(slug)); }
export function markFeatureComplete(slug, feature) {
  const done = getCompletion(slug);
  if (done[feature]) return { already: true, count: Object.keys(done).length };
  done[feature] = Date.now();
  writeJSON(DONE_KEY(slug), done);
  return { already: false, count: Object.keys(done).length };
}
export function completionStats(slug, features) {
  const done = getCompletion(slug);
  const owned = COMPLETION_FEATURES.filter((f) => (features || []).includes(f) || f === 'upload');
  const completed = owned.filter((f) => done[f]);
  const remaining = owned.filter((f) => !done[f]);
  return {
    total: owned.length,
    completed: completed.length,
    completedKeys: completed,
    nextFeature: remaining[0] || null,
    done,
  };
}
